import { useEffect, useRef } from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import AuditPopup from "@/components/AuditPopup";
import AuditAnnouncement from "@/components/AuditAnnouncement";
import SalesHero from "@/components/website/SalesHero";
import SalesProblem from "@/components/website/SalesProblem";
import SalesJourney from "@/components/website/SalesJourney";
import SalesDemo from "@/components/website/SalesDemo";
import SalesWhatWeCreate from "@/components/website/SalesWhatWeCreate";
import SalesCapabilities from "@/components/website/SalesCapabilities";
import SalesWhoFor from "@/components/website/SalesWhoFor";
import SalesComparison from "@/components/website/SalesComparison";
import SalesPlanCTA from "@/components/website/SalesPlanCTA";
import SalesWhy from "@/components/website/SalesWhy";
import SalesFAQ, { SALES_FAQS } from "@/components/website/SalesFAQ";
import SalesForm from "@/components/website/SalesForm";
import SectionRail from "@/components/sections/SectionRail";
import ReadingProgress from "@/components/sections/ReadingProgress";
import { trackEvent } from "@/lib/tracking";
import { usePageMeta } from "@/lib/meta";
import { useHashScroll } from "@/lib/useHashScroll";
import MobileStickyCta from "@/components/MobileStickyCta";

const PAGE_URL = "https://campaigns.rekisa.co.za/sales-system";
const TITLE = "Lead Generation Websites & Sales Systems | Rekisa";
const DESCRIPTION = "Rekisa builds business websites with lead capture, CRM, automated follow-up and sales pipelines. Get a connected sales system for your business.";
const ORG_ID = "https://campaigns.rekisa.co.za/#business";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${PAGE_URL}#webpage`,
      "url": PAGE_URL,
      "name": TITLE,
      "description": DESCRIPTION,
      "inLanguage": "en-ZA",
      "publisher": { "@id": ORG_ID },
      "about": { "@id": `${PAGE_URL}#service` },
      "primaryImageOfPage": "https://campaigns.rekisa.co.za/media/sales-system-og.jpg",
    },
    {
      "@type": "Service",
      "@id": `${PAGE_URL}#service`,
      "name": "Rekisa Sales System",
      "serviceType": "Business website development with CRM, lead capture and automated follow-up",
      "url": PAGE_URL,
      "provider": { "@id": ORG_ID },
      "areaServed": { "@type": "Country", "name": "South Africa" },
      "audience": { "@type": "BusinessAudience", "name": "South African SMEs, entrepreneurs and B2B service businesses" },
      "description": "Rekisa builds business websites and landing pages connected to lead capture, CRM, automated follow-up and sales pipelines. Pricing is provided in a tailored implementation proposal.",
    },
    {
      "@type": "FAQPage",
      "@id": `${PAGE_URL}#faq`,
      "mainEntity": SALES_FAQS.map(([q, a]) => ({ "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a } })),
    },
  ],
};

export default function SalesSystem() {
  usePageMeta({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    ogImage: "https://campaigns.rekisa.co.za/media/sales-system-og.jpg",
    ogImageAlt: "Rekisa Sales System: business websites connected to lead capture, CRM, automated follow-up and a sales pipeline",
    jsonLd: JSON_LD,
  });
  useHashScroll();

  const scrollMarks = useRef({ 50: false, 75: false, 90: false });
  const pricingViewed = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      [50, 75, 90].forEach((mark) => {
        if (pct >= mark && !scrollMarks.current[mark]) {
          scrollMarks.current[mark] = true;
          trackEvent(`scroll_${mark}`, { service: "website_app" });
        }
      });
      const inView = (id, ref, eventName) => {
        if (ref.current) return;
        const el = document.querySelector(`[data-testid=${id}]`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          ref.current = true;
          trackEvent(eventName, { service: "website_app" });
        }
      };
      inView("web-pricing-section", pricingViewed, "pricing_section_view");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-white text-zinc-900">
      <AuditAnnouncement service="website_app_creation" />
      <Header />
      <ReadingProgress
        sections={[
          { id: "web-hero-section", label: "Top" },
          { id: "web-problem-section", label: "Problems" },
          { id: "sales-demo-section", label: "Demo" },
          { id: "web-builds-section", label: "Solutions" },
          { id: "web-benefits-section", label: "Features" },
          { id: "sales-journey-section", label: "How it works" },
          { id: "sales-whofor-section", label: "Who it's for" },
          { id: "web-pricing-section", label: "Pricing" },
          { id: "web-faq-section", label: "FAQs" },
          { id: "web-quote-form-section", label: "Get a Plan" },
        ]}
      />
      <SectionRail
        service="website_app"
        sections={[
          { id: "web-hero-section", label: "Top" },
          { id: "web-problem-section", label: "Problems" },
          { id: "sales-demo-section", label: "Demo" },
          { id: "web-builds-section", label: "Solutions" },
          { id: "web-benefits-section", label: "Features" },
          { id: "sales-journey-section", label: "How it works" },
          { id: "sales-whofor-section", label: "Who it's for" },
          { id: "web-pricing-section", label: "Pricing" },
          { id: "web-faq-section", label: "FAQs" },
          { id: "web-quote-form-section", label: "Get a Plan" },
        ]}
      />
      <main>
        <SalesHero />
        <SalesProblem />
        <SalesDemo />
        <SalesWhatWeCreate />
        <SalesCapabilities />
        <SalesJourney />
        <SalesWhoFor />
        <SalesComparison />
        <SalesPlanCTA />
        <SalesWhy />
        <SalesFAQ />
        <SalesForm />
      </main>
      <MobileStickyCta label="Get a Plan" targetId="request-plan" heroId="home" service="website_app_creation" className="bg-[#BEF264] text-[#0B1120]" />
      <Footer />
      <AuditPopup service="website_app_creation" />
    </div>
  );
}
